import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Chip, IconButton, MenuItem, Select, Skeleton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import {
  customFieldDisplayName,
  CustomFieldRecord,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { Button } from "@/shared/foundation/primitives";

import { formatCustomFieldValue } from "./customFieldValueFormat";
import { CustomFieldValueInput } from "./CustomFieldValueInput";
import styles from "./CustomFieldValues.module.css";
import { isCommittableValue } from "./customFieldValueValidation";

export interface CustomFieldValuesTableProps {
  entityType: TargetEntityType;
  readyFields: CustomFieldRecord[];

  /**
   * Whether the Ready field definitions are still loading. While true, the
   * section shows a skeleton rather than the "no custom fields defined"
   * placeholder.
   */
  isLoading?: boolean;

  /** Current values by field name; a `null`/absent value renders no row. */
  values: Record<string, unknown>;

  /** Set (or replace) a field's value. */
  onSet: (fieldName: string, value: unknown) => void;

  /** Clear a field's value. */
  onClear: (fieldName: string) => void;

  /**
   * Section heading (defaults to `"Custom Fields"`). Pass `null` to omit the
   * heading row when the host supplies its own label.
   */
  heading?: string | null;

  /**
   * How the section frames itself against its host. `"card"` renders a
   * self-contained bordered, rounded, padded surface with row separators,
   * `"plain"` drops all of that so the section sits flush inside a
   * borderless list.
   */
  variant: "card" | "plain";
}

const SKELETON_ROW_COUNT = 2;

interface DraftState {
  fieldName: string;
  value: unknown;
}

function hasValue(values: Record<string, unknown>, fieldName: string): boolean {
  return values[fieldName] !== null && values[fieldName] !== undefined;
}

function defaultValueFor(field: CustomFieldRecord): unknown {
  return field.field_type === CustomFieldType.Boolean ? false : null;
}

/**
 * Editable table of an entity's custom-field values: one row per set value, an
 * inline row for adding another, and inline edit/clear controls per row.
 *
 * Presentational — the host owns the values and passes `onSet`/`onClear`.
 */
export function CustomFieldValuesTable({
  entityType,
  readyFields,
  isLoading = false,
  values,
  onSet,
  onClear,
  heading = "Custom Fields",
  variant,
}: CustomFieldValuesTableProps) {
  const [editDraft, setEditDraft] = React.useState<DraftState | null>(null);
  const [addDraft, setAddDraft] = React.useState<DraftState | null>(null);

  const entityPlural = `${entityType}s`;

  // Briefly highlight a newly added row, so the eye can follow it to its slot
  // (it doesn't land at the bottom). Decorative; the animation is gated on
  // reduced-motion.
  const [highlightedField, setHighlightedField] = React.useState<string | null>(
    null,
  );
  const highlightTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  React.useEffect(
    () => () => {
      if (highlightTimer.current) {
        clearTimeout(highlightTimer.current);
      }
    },
    [],
  );

  function flashRow(fieldName: string) {
    setHighlightedField(fieldName);
    if (highlightTimer.current) {
      clearTimeout(highlightTimer.current);
    }
    highlightTimer.current = setTimeout(() => setHighlightedField(null), 1600);
  }

  const setFields = readyFields.filter((field) =>
    hasValue(values, field.field_name),
  );

  const unsetFields = readyFields.filter(
    (field) => !hasValue(values, field.field_name),
  );

  function fieldByName(fieldName: string): CustomFieldRecord | undefined {
    return readyFields.find((field) => field.field_name === fieldName);
  }

  function startEdit(field: CustomFieldRecord) {
    setAddDraft(null);
    setEditDraft({
      fieldName: field.field_name,
      value: values[field.field_name],
    });
  }

  function confirmEdit() {
    const field = editDraft && fieldByName(editDraft.fieldName);
    if (editDraft && field && isCommittableValue(field, editDraft.value)) {
      onSet(editDraft.fieldName, editDraft.value);
      setEditDraft(null);
    }
  }

  function openAdd() {
    setEditDraft(null);
    const first = unsetFields[0];
    if (first) {
      setAddDraft({
        fieldName: first.field_name,
        value: defaultValueFor(first),
      });
    }
  }

  function confirmAdd() {
    const field = addDraft && fieldByName(addDraft.fieldName);
    if (addDraft && field && isCommittableValue(field, addDraft.value)) {
      onSet(addDraft.fieldName, addDraft.value);
      flashRow(addDraft.fieldName);
      setAddDraft(null);
    }
  }

  const addField = addDraft && fieldByName(addDraft.fieldName);

  return (
    <section className={classNames(styles.section, styles[variant])}>
      {heading !== null && (
        <div className={styles.headingRow}>
          <h3 className={styles.heading}>{heading}</h3>
        </div>
      )}

      {isLoading && (
        // Skeleton built from the real row/cell classes so its columns line up
        // with the values that replace it.
        <table className={styles.table} aria-busy="true">
          <caption className="sr-only">Loading custom fields…</caption>
          <tbody>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
              <tr key={rowIdx} className={styles.propertyRow}>
                <th scope="row" className={styles.propertyLabel}>
                  <Skeleton variant="text" width="60%" animation="wave" />
                </th>
                <td className={styles.valueCell}>
                  <div className={styles.valueContent}>
                    <Skeleton variant="text" width="10rem" animation="wave" />
                  </div>
                </td>
                <td className={styles.actionsCell} />
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isLoading && readyFields.length === 0 && (
        <p className={styles.placeholder}>
          No custom fields defined for {entityPlural}.
        </p>
      )}

      {!isLoading && readyFields.length > 0 && (
        <>
          {(setFields.length > 0 || addDraft) && (
            <table className={styles.table}>
              <caption className="sr-only">Custom field values</caption>
              <thead className="sr-only">
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Value</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {setFields.map((field) => {
                  const editing =
                    editDraft?.fieldName === field.field_name
                      ? editDraft
                      : null;

                  return (
                    <tr
                      key={field.field_name}
                      className={classNames(styles.propertyRow, {
                        [styles.rowHighlight]:
                          highlightedField === field.field_name,
                      })}
                      onKeyDown={
                        editing
                          ? (event) => {
                              if (event.key === "Escape") {
                                event.preventDefault();
                                setEditDraft(null);
                              }
                            }
                          : undefined
                      }
                    >
                      {/* Row header: labels its value cell for screen readers. */}
                      <th scope="row" className={styles.propertyLabel}>
                        {customFieldDisplayName(field)}
                      </th>
                      <td className={styles.valueCell}>
                        <div
                          className={classNames(styles.valueContent, {
                            [styles.valueContentEditing]: editing,
                          })}
                        >
                          {editing ? (
                            <CustomFieldValueInput
                              field={field}
                              value={editing.value}
                              onChange={(value) =>
                                setEditDraft({
                                  fieldName: field.field_name,
                                  value,
                                })
                              }
                              onCommit={confirmEdit}
                              autoFocus
                            />
                          ) : (
                            <ValueDisplay
                              field={field}
                              value={values[field.field_name]}
                            />
                          )}
                        </div>
                      </td>
                      <td className={styles.actionsCell}>
                        {editing ? (
                          <ConfirmCancel
                            committable={isCommittableValue(
                              field,
                              editing.value,
                            )}
                            onConfirm={confirmEdit}
                            onCancel={() => setEditDraft(null)}
                          />
                        ) : (
                          <div className={styles.rowActions}>
                            <IconButton
                              size="small"
                              aria-label={`Edit ${customFieldDisplayName(field)}`}
                              onClick={() => startEdit(field)}
                            >
                              <EditIcon color="primary" fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label={`Clear ${customFieldDisplayName(field)}`}
                              onClick={() => onClear(field.field_name)}
                            >
                              <DeleteIcon color="secondary" fontSize="small" />
                            </IconButton>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Add row: no field is chosen yet, so its first cell is the
                    picker (not a row header); it shares the same columns as the
                    display rows, keeping the picker/name and input/value aligned. */}
                {addDraft && addField && (
                  <tr
                    className={styles.propertyRow}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        setAddDraft(null);
                      }
                    }}
                  >
                    <td className={styles.pickerCell}>
                      <FieldPicker
                        fields={unsetFields}
                        selected={addDraft.fieldName}
                        onSelect={(fieldName) => {
                          const picked = fieldByName(fieldName);
                          setAddDraft({
                            fieldName,
                            value: picked ? defaultValueFor(picked) : null,
                          });
                        }}
                      />
                    </td>
                    <td className={styles.valueCell}>
                      <div
                        className={classNames(
                          styles.valueContent,
                          styles.valueContentEditing,
                        )}
                      >
                        <CustomFieldValueInput
                          // Remount on picker switch so the Number input's local
                          // text buffer resets. Edit rows get this from the <tr key>.
                          key={addField.field_name}
                          field={addField}
                          value={addDraft.value}
                          onChange={(value) =>
                            setAddDraft({
                              fieldName: addDraft.fieldName,
                              value,
                            })
                          }
                          onCommit={confirmAdd}
                          autoFocus
                        />
                      </div>
                    </td>
                    <td className={styles.actionsCell}>
                      <ConfirmCancel
                        committable={isCommittableValue(
                          addField,
                          addDraft.value,
                        )}
                        onConfirm={confirmAdd}
                        onCancel={() => setAddDraft(null)}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {!addDraft && unsetFields.length > 0 && (
            <div className={styles.footer}>
              <Button
                variant="text"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openAdd}
              >
                Set custom field
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Display rendering for a set value: enums as an outlined chip (a single token,
 * capped with an ellipsis + title tooltip), strings as wrapping text, and the
 * short scalar types (number/boolean/timestamp) as single-line text. */
function ValueDisplay({
  field,
  value,
}: {
  field: CustomFieldRecord;
  value: unknown;
}) {
  if (field.field_type === CustomFieldType.Enum) {
    return (
      <Chip
        className={styles.enumChip}
        label={String(value)}
        title={String(value)}
        size="small"
        variant="outlined"
      />
    );
  }

  const textClassName =
    field.field_type === CustomFieldType.String
      ? styles.valueText
      : `${styles.valueText} ${styles.valueTextSingleLine}`;

  return (
    <span className={textClassName}>
      {formatCustomFieldValue(field, value)}
    </span>
  );
}

/** The explicit ✓ confirm / ✕ cancel buttons shared by inline edit and the add
 * row (each in the row's controls slot). Confirm is disabled until the draft is
 * valid. */
function ConfirmCancel({
  committable,
  onConfirm,
  onCancel,
}: {
  committable: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.editActions}>
      <IconButton
        size="small"
        className={styles.confirmButton}
        aria-label="Confirm"
        disabled={!committable}
        onClick={onConfirm}
      >
        <CheckIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="Cancel" onClick={onCancel}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

interface FieldPickerProps {
  fields: CustomFieldRecord[];
  selected: string;
  onSelect: (fieldName: string) => void;
}

/**
 * Dropdown of not-yet-set fields, by display name, for the add row.
 *
 * The menu keeps MUI's default portal target, `document.body`. MUI mounts the
 * menu in a `position: fixed` overlay and places the options within it at
 * coordinates read from the trigger's viewport rect, and a transformed
 * ancestor becomes the containing block for a fixed descendant. Portalling the
 * menu into the table instead, under a popover host that positions its panel
 * with a translate, re-bases those coordinates on the panel and pushes the
 * options a panel-offset off-screen.
 *
 * A body portal does not turn an option click into an outside click for such a
 * host: React propagates events through the React tree rather than the DOM
 * tree, so the click still reaches the host's handlers as an inside one.
 */
function FieldPicker({ fields, selected, onSelect }: FieldPickerProps) {
  return (
    <Select
      size="small"
      fullWidth
      className={styles.fieldPicker}
      value={selected}
      onChange={(event) => onSelect(event.target.value)}
      aria-label="Select a custom field"
    >
      {fields.map((field) => (
        <MenuItem key={field.field_name} value={field.field_name}>
          {customFieldDisplayName(field)}
        </MenuItem>
      ))}
    </Select>
  );
}
